import { api } from '@/lib/api'
import type { LoginCredentials, LoginResponse } from '@/types/user'

/**
 * Servicio de autenticación
 */
export class AuthService {
	/**
	 * Realiza el login del usuario
	 * Nota: Este endpoint no incluye headers de autenticación ya que el usuario aún no está logueado
	 */
	static async login(credentials: LoginCredentials): Promise<LoginResponse> {
		const response = await api.post<LoginResponse>('/auth/login', credentials)
		const apiResponse = response.data

		// Verificar si la respuesta es exitosa
		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al iniciar sesión')
		}

		return apiResponse
	}

	/**
	 * Guarda el token de autenticación en localStorage
	 * (Si en el futuro la API devuelve un token)
	 */
	static saveToken(token: string): void {
		localStorage.setItem('auth_token', token)
	}

	/**
	 * Obtiene el token de autenticación
	 */
	static getToken(): string | null {
		return localStorage.getItem('auth_token')
	}

	/**
	 * Elimina el token de autenticación
	 */
	static removeToken(): void {
		localStorage.removeItem('auth_token')
	}

	/**
	 * Guarda la información del usuario en localStorage
	 */
	static saveUser(user: LoginResponse['data']): void {
		localStorage.setItem('user', JSON.stringify(user))
	}

	/**
	 * Obtiene la información del usuario desde localStorage
	 */
	static getUser(): LoginResponse['data'] | null {
		const userStr = localStorage.getItem('user')
		if (!userStr) return null
		try {
			return JSON.parse(userStr)
		} catch {
			return null
		}
	}

	/**
	 * Elimina la información del usuario
	 */
	static removeUser(): void {
		localStorage.removeItem('user')
	}

	/**
	 * Cierra la sesión del usuario
	 */
	static logout(): void {
		AuthService.removeToken()
		AuthService.removeUser()
	}

	/**
	 * Refresca la sesión del usuario
	 * Verifica si la sesión sigue activa y actualiza los datos del usuario
	 */
	static async refreshSession(): Promise<LoginResponse> {
		const response = await api.get<LoginResponse>('/auth/refresh-session')
		const apiResponse = response.data

		// Verificar si la respuesta es exitosa
		if (!apiResponse.success) {
			throw new Error(apiResponse.message || 'Error al refrescar sesión')
		}

		return apiResponse
	}
}
